import { ArrowUpRight, Timer } from "lucide-react";
import type { FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { StatusBadge } from "@/components/ui/status-badge";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import type { LawyerReducer } from "../../../redux/lawyer/lawyerSlice";
import { useCaseTime } from "../../timeTracking/queries";
import { formatHours } from "../../timeTracking/utils";
import { useCaseIdeOptional } from "../context/CaseIdeContext";
import { caseWorkspacePath } from "../routes";
import { RowLabel, RowValueButton } from "./chips/chip";

/** The tracker sends time each minute; the case's suggestions follow. */
export const TIME_REFRESH_MS = 2 * 60_000;

type Props = {
  individualId: string;
  foreignNationalId?: string | null;
  /** Called after the Time tab opens, so the popover can close. */
  onOpen?: () => void;
};

/** The details popover's Time row: the case's hours and what waits for review. Hidden while tracking is off. */
export const TimeRow: FC<Props> = ({
  individualId,
  foreignNationalId,
  onOpen
}) => {
  const enabled = useSelector((s: LawyerReducer) =>
    lawyerSelectors.selectTimeTrackingEnabled(s)
  );
  const ide = useCaseIdeOptional();
  const navigate = useNavigate();
  const { data } = useCaseTime(individualId, enabled, TIME_REFRESH_MS);
  if (!enabled) return null;
  const suggested = data?.my_unreleased_hours ?? 0;
  const request = { kind: "time", id: individualId, title: "Time" } as const;

  return (
    <div className="flex h-8 items-center gap-1">
      <RowLabel>
        <Timer className="size-3.5 text-fg-3" /> Time
      </RowLabel>
      <RowValueButton
        data-testid="time-row"
        title={
          suggested
            ? `${formatHours(
                suggested
              )} suggested from your work, waiting for your review`
            : "Open the case's time"
        }
        onClick={() => {
          if (ide) ide.open(request);
          else
            navigate(
              caseWorkspacePath({ individualId, foreignNationalId }, request)
            );
          onOpen?.();
        }}
      >
        <span className="tabular-nums">
          {data ? formatHours(data.total_hours) : "—"}
        </span>
        {suggested ? (
          <StatusBadge tone="accent" data-testid="time-row-suggested">
            {formatHours(suggested)} to review
          </StatusBadge>
        ) : null}
        <ArrowUpRight className="ml-auto size-3 opacity-0 transition-opacity group-hover/row:opacity-60" />
      </RowValueButton>
    </div>
  );
};
